import {
  PAGE_MAX,
  PAGE_SIZES,
  TABLE_PAGE_SIZE,
  pageHolding,
  type PageSize,
  type TableId,
} from '@myrewards/contracts';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef, type RefObject } from 'react';
import {
  UNSAFE_DataRouterContext,
  useLocation,
  useNavigation,
  useSearchParams,
  type DataRouter,
} from 'react-router';
import { mySettingsQuery, updateMySettings } from '../api';
import { prefersReducedMotion } from '../design/motion';

export interface Paging {
  page: number;
  size: PageSize;
  /**
   * The page on its way while the router loads this page's address with another page or size of
   * this table (its loader runs before the address changes, so the rows being read stay); null
   * otherwise. Give it to the Pager through `pagerWaiting`.
   */
  loading: number | null;
  /**
   * `quiet` is for corrections the screen makes itself (a clamped page): replace, no focus, no
   * scroll. `also` sets (or, with null, drops) other params in the same navigation.
   */
  setPage: (n: number, opts?: { quiet?: boolean; also?: Record<string, string | null> }) => void;
  /** Change the rows per page; `firstRowIndex` (0-based) keeps the row at the top in view. */
  setSize: (s: PageSize, firstRowIndex: number) => void;
  reset: () => void;
  /** The size is settled (address, or settings loaded or failed): safe to fetch the list. */
  ready: boolean;
  /** Put on the table's heading or wrapper (tabIndex -1): focus and scroll land here after a page change. */
  anchorRef: RefObject<HTMLElement | null>;
}

function sizeOf(value: string | null): PageSize | null {
  const n = Number(value);
  return PAGE_SIZES.find((s) => s === n) ?? null;
}

/**
 * The page and rows per page an address asks for: the address first, then the person's saved
 * choice, then the table's own size. A route loader uses this to fetch what the page will ask for.
 * A page past `PAGE_MAX` reads as `PAGE_MAX`; the server then sends the last page.
 */
export function pagingFromAddress(
  params: URLSearchParams,
  table: TableId,
  saved: Partial<Record<TableId, PageSize>> | undefined,
  opts: { param?: string; sizeParam?: string } = {},
): { page: number; size: PageSize; addressSize: PageSize | null } {
  const { param = 'page', sizeParam = 'size' } = opts;
  const requested = Number(params.get(param));
  const addressSize = sizeOf(params.get(sizeParam));
  return {
    page: Number.isInteger(requested) && requested >= 1 ? Math.min(requested, PAGE_MAX) : 1,
    size: addressSize ?? saved?.[table] ?? TABLE_PAGE_SIZE[table],
    addressSize,
  };
}

/**
 * What a route loader fetches for a paged table: the page and rows per page `usePaging` will settle
 * on (the address, then the person's saved choice, then the table's own size), so the page's own
 * query finds the loader's answer under the same key. The saved choice is read through the query
 * client (the shell has it cached); if it can't be read, the table's own size is used, as
 * `usePaging` does. Every loader that pages resolves its page and size here, never on its own.
 */
export async function loaderPaging(
  queryClient: QueryClient,
  params: URLSearchParams,
  table: TableId,
  opts: { param?: string; sizeParam?: string } = {},
): Promise<{ page: number; size: PageSize }> {
  const saved =
    sizeOf(params.get(opts.sizeParam ?? 'size')) === null
      ? (await queryClient.query(mySettingsQuery).catch(() => undefined))?.tablePageSizes
      : undefined;
  const { page, size } = pagingFromAddress(params, table, saved, opts);
  return { page, size };
}

/**
 * The page the router is bringing in for this table: while it loads the same path with this table's
 * page or size changed, the target's page (read as `pagingFromAddress` reads it); otherwise null.
 * The size leaving the address is no change: after a save it goes (§7) and the rows stay the same.
 */
function pageOnItsWay(
  navigation: ReturnType<typeof useNavigation>,
  pathname: string,
  current: URLSearchParams,
  table: TableId,
  opts: { param: string; sizeParam: string },
): number | null {
  if (navigation.state !== 'loading' || navigation.location.pathname !== pathname) return null;
  const target = new URLSearchParams(navigation.location.search);
  const { param, sizeParam } = opts;
  const targetSize = target.get(sizeParam);
  if (
    target.get(param) === current.get(param) &&
    (targetSize === null || targetSize === current.get(sizeParam))
  )
    return null;
  return pagingFromAddress(target, table, undefined, opts).page;
}

/**
 * Calls `then` once the router has nothing on its way: no navigation and no revalidation. A
 * navigation started now would cut either short, and the person's click with it (APP-216).
 *
 * It reads the router itself, never `useNavigation()`: the router hands each change to React
 * inside `startTransition`, so a render can still say "idle" while a navigation is under way.
 * Nothing navigates from inside the router's own subscriber call (it is still finishing the
 * navigation then); the check runs again a microtask later. Returns a function that stops waiting.
 */
function whenRouterSettles(router: DataRouter, then: () => void): () => void {
  let stopped = false;
  let unsubscribe: (() => void) | null = null;
  const settled = (): boolean =>
    router.state.navigation.state === 'idle' && router.state.revalidation === 'idle';
  const check = (): void => {
    if (stopped) return;
    if (settled()) {
      stopped = true;
      then();
      return;
    }
    unsubscribe ??= router.subscribe(() => {
      if (!settled()) return;
      unsubscribe?.();
      unsubscribe = null;
      queueMicrotask(check);
    });
  };
  check();
  return () => {
    stopped = true;
    unsubscribe?.();
  };
}

/**
 * What a Pager shows while a page is on its way (APP-135 §1): the rows on screen stay, the count
 * names the page coming, and the controls wait. `pending` is the list's own wait (a placeholder
 * held in place, or a deferred page); the router's comes from `paging.loading`.
 */
export function pagerWaiting(
  paging: Pick<Paging, 'page' | 'loading'>,
  pending: boolean,
): { pending: boolean; loadingPage: number } {
  return {
    pending: pending || paging.loading !== null,
    loadingPage: paging.loading ?? paging.page,
  };
}

/**
 * A table's page and rows per page, kept in the address (`?page=3&size=100`) so Back, reload and
 * shared links land on the same page. The size starts from the address, then the person's saved
 * choice, then the table's own size; picking one saves it for the person (APP-135).
 */
export function usePaging(
  table: TableId,
  opts: { param?: string; sizeParam?: string } = {},
): Paging {
  const { param = 'page', sizeParam = 'size' } = opts;
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const settings = useQuery(mySettingsQuery);
  const anchorRef = useRef<HTMLElement | null>(null);
  // The router itself (useNavigation() already requires one), for where it really is now.
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  if (dataRouter === null) throw new Error('usePaging needs a data router.');
  const { router } = dataRouter;
  // A saved size waiting for the router to settle before it leaves the address; stopped on unmount.
  const stopWaiting = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      stopWaiting.current?.();
    },
    [],
  );

  const { page, size, addressSize } = pagingFromAddress(
    params,
    table,
    settings.data?.tablePageSizes,
    { param, sizeParam },
  );
  const ready = addressSize !== null || !settings.isPending;
  const loading = pageOnItsWay(navigation, pathname, params, table, { param, sizeParam });

  const setPage = useCallback(
    (n: number, opts?: { quiet?: boolean; also?: Record<string, string | null> }) => {
      const quiet = opts?.quiet === true;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(opts?.also ?? {}))
            if (value === null) next.delete(key);
            else next.set(key, value);
          if (n <= 1) next.delete(param);
          else next.set(param, String(n));
          return next;
        },
        { replace: quiet },
      );
      if (quiet) return;
      const anchor = anchorRef.current;
      if (anchor) {
        anchor.focus({ preventScroll: true });
        anchor.scrollIntoView({
          block: 'start',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }
    },
    [param, setParams],
  );

  /**
   * Once a size is saved it leaves the address (§7), from wherever the router is by then, and
   * never by cutting short a navigation the person started meanwhile (APP-216): a name opened, a
   * page picked or another screen. The save can land while such a navigation's loader is still
   * reading; a navigation started then would cancel it, so the click would come to nothing.
   */
  const dropSavedSize = useCallback(
    (saved: string) => {
      stopWaiting.current?.();
      stopWaiting.current = whenRouterSettles(router, () => {
        stopWaiting.current = null;
        const { location } = router.state;
        if (location.pathname !== pathname) return;
        const current = new URLSearchParams(location.search);
        if (current.get(sizeParam) !== saved) return;
        current.delete(sizeParam);
        setParams(current, { replace: true });
      });
    },
    [pathname, router, setParams, sizeParam],
  );

  const setSize = useCallback(
    (s: PageSize, firstRowIndex: number) => {
      const holding = pageHolding(firstRowIndex, s);
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (holding <= 1) next.delete(param);
          else next.set(param, String(holding));
          next.set(sizeParam, String(s));
          return next;
        },
        { replace: true },
      );
      // Fire and forget. If the save fails (Preview as is read-only) the size stays in the address.
      void updateMySettings({ tablePageSizes: { [table]: s } })
        .then((saved) => {
          queryClient.setQueryData(mySettingsQuery.queryKey, saved);
          dropSavedSize(String(s));
        })
        .catch(() => undefined);
    },
    [dropSavedSize, param, queryClient, setParams, sizeParam, table],
  );

  const reset = useCallback(() => {
    setParams(
      (prev) => {
        if (!prev.has(param)) return prev;
        const next = new URLSearchParams(prev);
        next.delete(param);
        return next;
      },
      { replace: true },
    );
  }, [param, setParams]);

  return { page, size, loading, setPage, setSize, reset, ready, anchorRef };
}
